import { useState } from 'react';

import {
  assetColumns,
  assetDefaultSort,
} from '@/sections/attacks/attackPaths/components/wizards/columns/assetColumns';
import { SelectionTable } from '@/sections/attacks/attackPaths/components/wizards/components/SelectionTable';
import { WizardSearchInput } from '@/sections/attacks/attackPaths/components/wizards/components/WizardSearchInput';
import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';
import { useSearchableList } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';
import {
  CLOUD_SCOPE_ASSET_CLASSES,
  EXTERNAL_SCOPE_ASSET_CLASSES,
} from '@/sections/insights/aiAssistant/components/huntScopeAssetClasses';

type HuntScopeKind = 'external' | 'cloud' | 'webapplication';

interface HuntScopeStepProps {
  /** Selected target keys. Empty ⇒ all active targets for this hunt type. */
  value: string[];
  onChange: (keys: string[]) => void;
  kind?: HuntScopeKind;
  /**
   * Explicit asset-class filter. Overrides the kind-derived default so a tabbed
   * view (HuntScopeTabs) can narrow one shared HuntScopeStep to a subset of the
   * pool. Omit to keep the kind-derived default (external vs cloud).
   */
  assetClasses?: string[];
  /**
   * Overrides the search input placeholder. Lets a tabbed view (HuntScopeTabs)
   * give each tab domain/CIDR-specific copy. Omit to keep the kind-derived
   * default (external vs cloud) byte-for-byte.
   */
  searchPlaceholder?: string;
  /**
   * Overrides the search input aria-label. Paired with searchPlaceholder for
   * per-tab copy. Omit to keep the kind-derived default byte-for-byte.
   */
  searchAriaLabel?: string;
}

/**
 * Scope step for the hunt launcher: a searchable, multi-select asset table that
 * lets the user restrict the hunt to specific assets. Reuses the same
 * search-input + selection-table stack as the scan-profile asset assignment
 * modal so the two flows feel identical.
 *
 * Layout model: this component fills a fixed-height parent (the dialog's scope
 * step body) as a flex column — the search input is a natural-height child and
 * the table flex-fills the remaining space via `fillHeight`. Because the table
 * fills (rather than using a fixed pixel height), its "Scroll for more entries"
 * indicator shrinks the scroll area instead of growing the dialog, so the
 * dialog stays the same height as every other step regardless of row count.
 *
 * Empty selection ⇒ the hunt is scoped to all active targets in Guard — this is
 * the default, so leaving the table untouched keeps the prior behaviour.
 */
export function HuntScopeStep({
  value,
  onChange,
  kind = 'external',
  assetClasses: assetClassesProp,
  searchPlaceholder,
  searchAriaLabel,
}: HuntScopeStepProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // value is the source of truth for selection; derive the Set the table needs.
  const selectedKeys = new Set(value);

  const isWebApplication = kind === 'webapplication';

  // Web hunts query WebApplication nodes directly (not :Asset), so the
  // asset-class fence does not apply. External/cloud hunts keep their
  // kind-derived class allow-list (overridable by HuntScopeTabs).
  const assetClasses = isWebApplication
    ? undefined
    : (assetClassesProp ??
      (kind === 'cloud'
        ? CLOUD_SCOPE_ASSET_CLASSES
        : EXTERNAL_SCOPE_ASSET_CLASSES));

  // Active-only scope fence: only active targets are selectable into a hunt
  // (which steers an autonomous agent). `STARTS WITH 'A'` covers the whole
  // active family (A/AL/AP/AH). External hunts get web-facing infrastructure;
  // cloud hunts get cloud account roots; web hunts get active WebApplications.
  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchableList({
      type: isWebApplication ? 'webapplication' : 'asset',
      searchTerm,
      pageSize: 200,
      assetStatuses: ['A'],
      assetClasses,
      // Use the Lucene fulltext index (node.search) — same matching the global
      // asset search bar uses — instead of CONTAINS substring filters. Not
      // enabled for WebApplications: the assetSearchIndex covers :Asset nodes,
      // so web hunts fall back to the CONTAINS OR-block over WebApplication
      // fields (key/name/primary_url/identifier/domain).
      fulltextSearch: !isWebApplication,
    });

  const handleToggle = (item: SearchableListItem) => {
    onChange(
      value.includes(item.key)
        ? value.filter(k => k !== item.key)
        : [...value, item.key]
    );
  };

  const handleSelectAllVisible = () => {
    const next = new Set(value);
    for (const item of items) next.add(item.key);
    onChange([...next]);
  };

  // Inverse of handleSelectAllVisible: remove only the currently visible
  // (filtered) rows, preserving any keys selected on other tabs. HuntScopeTabs
  // shares one scope[] across Assets/Domains/CIDRs, and SelectionTable's header
  // checkbox routes to onClearAll whenever any visible row is selected — so a
  // blanket onChange([]) here would wipe selections made on the other tabs.
  const handleClearAll = () => {
    const visibleKeys = new Set(items.map(item => item.key));
    onChange(value.filter(k => !visibleKeys.has(k)));
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <WizardSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={
          searchPlaceholder ??
          (kind === 'cloud'
            ? 'Search cloud accounts...'
            : isWebApplication
              ? 'Search web applications...'
              : 'Search assets...')
        }
        ariaLabel={
          searchAriaLabel ??
          (kind === 'cloud'
            ? 'Search cloud accounts to scope the hunt'
            : isWebApplication
              ? 'Search web applications to scope the hunt'
              : 'Search assets to scope the hunt')
        }
        autoFocus
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <SelectionTable
          mode="multi"
          data={items}
          columns={assetColumns}
          selectedKeys={selectedKeys}
          onToggle={handleToggle}
          onSelectAllVisible={handleSelectAllVisible}
          onClearAll={handleClearAll}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
          defaultSort={assetDefaultSort}
          fillHeight
        />
      </div>
    </div>
  );
}
